import React, {useEffect, useState} from 'react';
import {Box, fade, FormHelperText, makeStyles, Paper, TextField, Typography, useTheme} from '@material-ui/core';
import {PlainButton, SecondaryButton} from "Components/Button";
import {useLogin} from "Hooks/api/useLogin";
import {Controller, useForm} from "react-hook-form";
import * as yup from 'yup';
import {yupResolver} from '@hookform/resolvers/yup';
import PasswordField from "Components/PasswordField";
import {BP_LARGE, BP_MEDIUM} from "Theme/breakpoints";
import {shallowEqual, useDispatch, useSelector} from "react-redux";
import {Alert} from "@material-ui/lab";
import {clearLoginError} from "Redux/actions/ui";
import {FloatingHelpButton} from "Components/LiveChat";
import {MQMaxMedium, MQMinLarge} from "Helpers/mediaQueries";
import {BRAND_NAME, IMAGES_PATH, STORED_USERNAME} from "Setup/config";
import {HOME_URL, LOST_DETAILS_URL} from "Setup/urls";
import PromotionalTerms from "Components/PromotionalTerms";
import RouterLink from "Components/WrappedMUIComponents/RouterLink";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import {useLocation} from "react-router-dom";
import WebpImage from "Components/WebpImage";

const useLoginPageStyles = makeStyles(theme => {
    const LoginSettings = theme.overrides.LoginPage;

    return {
        root: {
            width: '100%',
            [theme.breakpoints.up(BP_LARGE)]: {
                height: '100vh',
                background: LoginSettings.formBackground,
            },
        },
        close: {
            position: 'absolute',
            right: 0,
            top: 0,
            zIndex: 1
        },
        loginForm: {
            width: '100%',
            maxWidth: 270,
            margin: theme.spacing(2, 'auto'),
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            '& .MuiTextField-root': {
                '& .MuiFormHelperText-root': {
                    color: theme.palette.error.main
                }
            },
            '& .MuiButton-text': {
                color: LoginSettings.linkColor,
            },
            [theme.breakpoints.up(BP_MEDIUM)]: {
                margin: theme.spacing(3, 'auto'),
            }
        },
        paper: {
            width: '100%',
            position: 'relative',
            border: 'none',
            height: '100vh',
            display: 'flex'
        },
        logo: {
            maxWidth: LoginSettings.sideLogoWidth,
            logoContrast: LoginSettings.logoContrast,
            paddingBottom: theme.spacing(4),
            [theme.breakpoints.up(BP_MEDIUM)]: {
                paddingTop: theme.spacing(5),
            },
        },
        sideContent: {
            backgroundSize: 'cover',
            maxWidth: 600,
            minWidth: 450,
            width: '30vw',
            height: '100vh',
            textAlign: 'center',
            overflow: 'hidden',
            color: LoginSettings.sideContrastColor,
        },
        loginImage: {
            width: '100%',
            height: '70vh',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center center',
            marginTop: theme.spacing(2),
            backgroundSize: 'contain',
        },
        mainContent: {
            flex: 1,
            alignSelf: 'center',
            textAlign: 'center',
            '& .MuiTypography-h4': {
                color: LoginSettings.formContrastColor
            },
            '& .MuiTypography-h6': {
                color: LoginSettings.formContrastColor,
                fontSize: '1rem',
                marginBottom: theme.spacing(1),
            },
            padding: theme.spacing(3, 2),
            [theme.breakpoints.up(BP_MEDIUM)]: {
                padding: theme.spacing(5, 2),
                // height: '100%',
                borderRadius: `0 ${theme.spacing(3)}px ${theme.spacing(3)}px 0`,
                background: LoginSettings.formBackground,
            },

        },
        terms: {
            height: 150,
            overflowY: 'scroll',
            maskImage: 'linear-gradient(to bottom, black 60%, transparent 100%)',
            [theme.breakpoints.up(BP_MEDIUM)]: {
                width: 450,
                margin: '0 auto',
            },
            '& .MuiTypography-subtitle2': {
                fontSize: '0.75rem'
            },
            '& .MuiTypography-h6': {
                fontSize: '0.9rem'
            },
            '&::-webkit-scrollbar': {
                width: theme.spacing(1)
            },
            '&::-webkit-scrollbar-track': {
                backgroundColor: fade(theme.palette.neutral.main, 0.3),
                borderRadius: 4,
            },
            '&::-webkit-scrollbar-thumb': {
                backgroundColor: theme.palette.neutral.main,
                borderRadius: 4,
            }
        },
        loginHintsContainer: {
            width: "100%",
            margin: "0 auto",
            textAlign: "justify",
            fontSize: "80%",
            marginBottom: theme.spacing(0.5)
        },
        loginHints: {
            display: "block",
            lineHeight: 1.6
        },
        loginButton: {
            width: '100%'
        },
        loginRow: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: 'calc(100vh - 70px)',
        },
        helperText: {
            marginBottom: theme.spacing(2)
        }
    }
});

const LoginPage = (props) => {
    const classes = useLoginPageStyles();
    const dispatch = useDispatch();
    const theme = useTheme();
    let [welcomeBack, setWelcomeBack] = useState(false);
    let [pageDescription, setPageDescription] = useState(false);

    const location = useLocation();
    useEffect(() => {
        if (location.pathname.includes('welcome')) {
            setWelcomeBack(true);
        }
        if (location.pathname.includes('lost-password')) {
            setPageDescription(true);
        }

        console.log([location.pathname, location.search]);
    }, [location]);


    const {loginError, loginErrorMessage} = useSelector(state => {
        return {
            loginError: state?.ui?.loginError ? state.ui.loginError : false,
            loginErrorMessage: state?.ui?.loginErrorMessage ? state.ui.loginErrorMessage : false,
        }
    }, shallowEqual);

    const [error, setError] = useState(loginError);
    const [errorMessage, setErrorMessage] = useState(loginErrorMessage);

    useEffect(() => {
        document.body.classList.add("login-page");

        return () => {
            document.body.classList.remove("login-page");
        };
    }, []);

    const loginValidationSchema = yup.object({
        username: yup.string()
            .required('Username is required')
            .max(50, 'Max 50 characters'),
        // .matches(/^[a-z0-9]{2,50}$/i, 'Must not contain any special characters.'),

        password: yup.string()
            .required('Password is required')
            .min(8, 'Must be a minimum of 8 characters.')
            .matches(/^\S*$/, 'Must not contain any spaces.')
            .matches(/^\S*(?=\S*[a-z])\S*$/, 'Must contain at least one lowercase character.')
            .matches(/^\S*(?=\S*[A-Z])\S*$/, 'Must contain at least one uppercase character.')
            .matches(/^\S*(?=\S*[\d])\S*$/, 'Must contain at least one number.')
    })

    const {control, trigger, handleSubmit, getValues, formState: {errors}} = useForm({
        resolver: yupResolver(loginValidationSchema),
        mode: 'onTouched',
        reValidateMode: 'onChange',
        defaultValues: {
            username: STORED_USERNAME,
            password: ''
        }
    });

    const {isLoggingIn, login} = useLogin();

    useEffect(() => {
        if (loginError) {
            setError(loginError);
            setErrorMessage(loginErrorMessage);
            dispatch(clearLoginError());
        }
    }, [loginError]);

    useEffect(() => {
        const submitOnEnter = (e) => {
            if (e.keyCode === 13) {
                handleLogin();
            }
        }

        document.getElementById('login-form').addEventListener('keyup', submitOnEnter);

        return () => {
            document.getElementById('login-form').removeEventListener('keyup', submitOnEnter);
        }
    }, []);

    const submitForm = async () => {
        await login(getValues().username, getValues().password);
    };

    const onError = (errors) => {
        console.log('Error logging in:');
        console.log(errors);
    };

    const handleLogin = async () => {
        let initiateLogin = await trigger();

        if (initiateLogin) {
            console.log('logging in...');
            handleSubmit(submitForm, onError)();
        }
    };

    const usernameHints = [
        "You can only log in with your username.",
        "Emails and phone numbers are not accepted.",
    ];

    const passwordHints = [
        "Please note that your password must contain at least one uppercase and lowercase character and one number. If it doesn't, click 'Forgot your details?' below to reset it.",
    ];

    return (
        <main className={classes.root}>
            <RouterLink aria-label="close" to={HOME_URL} color="inherit">
                <IconButton className={classes.close} aria-label="close">
                    <CloseIcon/>
                </IconButton>
            </RouterLink>
            <FloatingHelpButton/>
            <Paper className={classes.paper}>
                <MQMinLarge>
                    <Box px={1} py={3} className={classes.sideContent} style={{backgroundImage: `url('${IMAGES_PATH}/login-page/login_page_bg.png')`}}>
                        <RouterLink aria-label="Home" to={HOME_URL} color="inherit" style={{display: "block"}}>
                            <WebpImage ignoreWebp className={classes.logo} src={theme.layout.logo} alt={`${BRAND_NAME}`}/>
                        </RouterLink>
                        <Typography variant="h6" component={'h3'} align={'center'}>Express Withdrawals</Typography>
                        <Typography variant="caption" component={'p'} gutterBottom align={'center'}>Enjoy next business day Visa<br/> withdrawals with Express
                            Withdrawals.<br/> T&Cs apply.</Typography>
                        <div className={classes.loginImage} style={{backgroundImage: `url('${IMAGES_PATH}/login-page/login_page_fg.png')`}}/>
                    </Box>
                </MQMinLarge>
                <Box className={classes.mainContent}>
                    <MQMaxMedium><WebpImage className={classes.logo} src={theme.layout.contrastLogo} alt={`${BRAND_NAME}`}/></MQMaxMedium>
                    {welcomeBack ?
                        <>
                            <Typography paragraph variant={'h5'} component={'h2'} align={'center'}>Welcome
                                back{STORED_USERNAME && `, ${STORED_USERNAME}`}</Typography>
                            <Typography paragraph variant={'h5'} component={'h2'} align={'center'}>Log In to continue</Typography>
                        </> :
                        <Typography paragraph variant={'h4'} component={'h2'} align={'center'}>Log In</Typography>
                    }

                    {error &&
                        <Box maxWidth={450} margin="0 auto">
                            <Alert severity="error">
                                {errorMessage}
                            </Alert>
                        </Box>
                    }

                    {pageDescription &&
                        <Box maxWidth={400} margin="0 auto">
                            <Alert severity="info">
                                <Typography variant="body2">It looks like you already have an account with us!</Typography>
                                <Typography variant="body2">Log In below to play.</Typography>
                            </Alert>
                        </Box>
                    }

                    <form className={classes.loginForm} autoComplete={'off'} id={'login-form'}>
                        <Box width="100%" mb={2}>
                            <Box className={classes.loginHintsContainer}>
                                {usernameHints.map((h, k) => (
                                    <Typography key={`un-hint-${k}`} variant="inherit" className={classes.loginHints}>
                                        {h}
                                    </Typography>
                                ))}
                            </Box>
                            <Controller
                                control={control}
                                name={'username'}
                                render={({onChange, value, name}) => (
                                    <TextField
                                        name={name}
                                        value={value}
                                        label="Username"
                                        variant="outlined"
                                        margin={'dense'}
                                        autoComplete={'Username'}
                                        fullWidth
                                        onChange={onChange}
                                        error={!!errors.username && !!errors.username.message}
                                        onBlur={() => trigger('username')}
                                    />
                                )}
                            />
                            {(!!errors.username && errors.username.message) &&
                                <FormHelperText error>{errors.username.message}</FormHelperText>
                            }
                        </Box>

                        <Box mb={2}>
                            <Box className={classes.loginHintsContainer}>
                                {passwordHints.map((h, k) => (
                                    <Typography key={`pw-hint-${k}`} variant="inherit" className={classes.loginHints}>
                                        {h}
                                    </Typography>
                                ))}
                            </Box>
                            <Controller
                                control={control}
                                name={'password'}
                                render={({onChange, value, name}) => (
                                    <PasswordField
                                        name={name}
                                        value={value}
                                        label="Password"
                                        onChange={onChange}
                                        fullWidth
                                        error={!!errors.password && !!errors.password.message}
                                        onBlur={() => trigger('password')}
                                    />
                                )}
                            />
                            {(!!errors.password && errors.password.message) &&
                                <FormHelperText error>{errors.password.message}</FormHelperText>
                            }
                        </Box>
                        <Box mb={2} width={"100%"}>
                            <SecondaryButton isLoading={isLoggingIn} onClick={handleLogin} className={classes.loginButton}>Log In</SecondaryButton>
                        </Box>
                        <PlainButton to={LOST_DETAILS_URL}>Forgot your password?</PlainButton>
                    </form>
                    {welcomeBack &&
                        <>
                            <Typography variant={'h6'} component={'p'} align={'center'}>Promotional Terms</Typography>
                            <Box className={classes.terms}>
                                <PromotionalTerms/>
                            </Box>
                        </>
                    }
                </Box>
            </Paper>
        </main>
    )
};

export default LoginPage;
